import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, TrendingUp, Brain, Cpu } from 'lucide-react';
import './Navbar.css';

/* ─── Service sub-items ───────────────────────────── */
const SERVICE_ITEMS = [
  { id: 'growth',       label: 'Aurelix Growth',       desc: 'Build. Acquire. Convert. Scale.',     href: '/growth',       icon: TrendingUp, accent: '#ec2b28' },
  { id: 'intelligence', label: 'Aurelix Intelligence', desc: 'Turn data into direction.',            href: '/intelligence', icon: Brain,      accent: '#3b82f6' },
  { id: 'ai',           label: 'Aurelix AI',           desc: 'AI built around your workflow.',       href: '/aurelix-ai',   icon: Cpu,        accent: '#a855f7' },
];

const CENTER_LINKS = [
  { id: 'services', label: 'Services', href: null,        hasDrop: true },
  { id: 'about',    label: 'About',    href: '/about',    hasDrop: false },
  { id: 'contact',  label: 'Contact',  href: '/contact',  hasDrop: false },
];

/* ─── Wing SVGs ─── */
function NotchLeftWing() {
  return (
    <svg className="nb-wing nb-wing--left" aria-hidden="true"
      width="20" height="20" viewBox="0 0 20 20" fill="none" shapeRendering="geometricPrecision">
      <path d="M 0 0 C 11.046 0 20 8.954 20 20 H 21 V -1 H 0 Z" fill="currentColor" />
    </svg>
  );
}

function NotchRightWing() {
  return (
    <svg className="nb-wing nb-wing--right" aria-hidden="true"
      width="20" height="20" viewBox="0 0 20 20" fill="none" shapeRendering="geometricPrecision">
      <path d="M 20 0 C 8.954 0 0 8.954 0 20 H -1 V -1 H 20 Z" fill="currentColor" />
    </svg>
  );
}

function NotchCornerLeftWing() {
  return (
    <svg className="nb-wing nb-corner--left" aria-hidden="true"
      width="20" height="20" viewBox="0 0 20 20" fill="none" shapeRendering="geometricPrecision">
      <path d="M 0 0 H 20 C 8.954 0 0 8.954 0 20 V 0 Z" fill="currentColor" />
    </svg>
  );
}

function NotchCornerRightWing() {
  return (
    <svg className="nb-wing nb-corner--right" aria-hidden="true"
      width="20" height="20" viewBox="0 0 20 20" fill="none" shapeRendering="geometricPrecision">
      <path d="M 20 0 H 0 C 11.046 0 20 8.954 20 20 V 0 Z" fill="currentColor" />
    </svg>
  );
}

export default function Navbar() {
  const [visible, setVisible]           = useState(true);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [mobServicesOpen, setMobServicesOpen] = useState(false);

  const lastY    = useRef(0);
  const dropRef  = useRef(null);
  const location = useLocation();

  const isServicesActive = SERVICE_ITEMS.some(s => s.href === location.pathname);

  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
    setMobServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setVisible(y < lastY.current || y < 80);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setServicesOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      {/* ══════════════ DESKTOP ══════════════ */}
      <div className={`nb-desktop${visible ? '' : ' nb-desktop--hidden'}`}>
        
        {/* LEFT — Logo Only (No notch) */}
        <aside className="nb-left-slot" aria-label="Brand logo">
          <Link to="/" className="nb-logo-link">
            <img src="/aurelix-logo.png" alt="Aurelix" className="nb-logo-img" />
          </Link>
        </aside>

        {/* CENTER NOTCH — Navigation Only */}
        <header className="nb-notch nb-notch--center" role="navigation">
          <NotchLeftWing />
          <NotchRightWing />
          
          <nav className="nb-nav-row">
            {CENTER_LINKS.map(link => (
              <div key={link.id} className="nb-nav-item" ref={link.hasDrop ? dropRef : null}>
                {link.hasDrop ? (
                  <>
                    <button
                      className={`nb-link nb-link--btn${isServicesActive ? ' nb-link--active' : ''}`}
                      onClick={() => setServicesOpen(o => !o)}
                      aria-expanded={servicesOpen}
                    >
                      {isServicesActive && (
                        <motion.span layoutId="nb-pill" className="nb-active-pill"
                          transition={{ type: 'spring', stiffness: 400, damping: 30 }} />
                      )}
                      <span className="nb-link-inner">
                        {link.label}
                        <ChevronDown size={13}
                          className={`nb-chevron${servicesOpen ? ' nb-chevron--open' : ''}`} />
                      </span>
                    </button>

                    {/* Dropdown */}
                    <AnimatePresence>
                      {servicesOpen && (
                        <motion.div className="nb-dropdown"
                          initial={{ opacity: 0, y: -6, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0,  scale: 1 }}
                          exit={{   opacity: 0, y: -6, scale: 0.97 }}
                          transition={{ duration: 0.16, ease: 'easeOut' }}
                        >
                          {SERVICE_ITEMS.map(s => {
                            const Icon = s.icon;
                            return (
                              <Link key={s.id} to={s.href} className="nb-drop-item">
                                <span className="nb-drop-icon"
                                  style={{ background: `${s.accent}18`, color: s.accent }}>
                                  <Icon size={15} />
                                </span>
                                <span className="nb-drop-text">
                                  <span className="nb-drop-name">{s.label}</span>
                                  <span className="nb-drop-desc">{s.desc}</span>
                                </span>
                              </Link>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link to={link.href}
                    className={`nb-link${location.pathname === link.href ? ' nb-link--active' : ''}`}>
                    {location.pathname === link.href && (
                      <motion.span layoutId="nb-pill" className="nb-active-pill"
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }} />
                    )}
                    <span className="nb-link-inner">{link.label}</span>
                  </Link>
                )}
              </div>
            ))}
          </nav>
        </header>

        {/* RIGHT — CTA Only (No notch) */}
        <aside className="nb-right-slot" aria-label="Call to action">
          <Link to="/contact" className="nb-cta">Let's talk</Link>
        </aside>

      </div>

      {/* ══════════════ MOBILE ══════════════ */}
      <div className={`nb-mobile${visible ? '' : ' nb-mobile--hidden'}`}>
        <div className="nb-m-notch">
          <NotchLeftWing />
          <NotchRightWing />
          <div className="nb-m-bar">
            <Link to="/" className="nb-logo-link">
              <img src="/aurelix-logo.png" alt="Aurelix" className="nb-logo-img" />
            </Link>

            <button className="nb-m-trigger" onClick={() => setMobileOpen(o => !o)}
              aria-expanded={mobileOpen}>
              <span>
                {SERVICE_ITEMS.find(s => s.href === location.pathname)?.label ||
                 CENTER_LINKS.find(l => l.href === location.pathname)?.label ||
                 'Menu'}
              </span>
              <ChevronDown size={13}
                className={`nb-chevron${mobileOpen ? ' nb-chevron--open' : ''}`} />
            </button>

            <Link to="/contact" className="nb-cta nb-cta--sm">Let's talk</Link>
          </div>

          <AnimatePresence>
            {mobileOpen && (
              <motion.div className="nb-m-dropdown"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{   opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}>

                <button className="nb-m-link nb-m-link--btn"
                  onClick={() => setMobServicesOpen(o => !o)}>
                  <span>Services</span>
                  <ChevronDown size={13}
                    className={`nb-chevron${mobServicesOpen ? ' nb-chevron--open' : ''}`} />
                </button>

                <AnimatePresence>
                  {mobServicesOpen && (
                    <motion.div className="nb-m-sub"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{   opacity: 0, height: 0 }}
                      transition={{ duration: 0.15 }}>
                      {SERVICE_ITEMS.map(s => {
                        const Icon = s.icon;
                        return (
                          <Link key={s.id} to={s.href} className="nb-m-sub-item"
                            onClick={() => setMobileOpen(false)}>
                            <span className="nb-drop-icon"
                              style={{ background: `${s.accent}18`, color: s.accent }}>
                              <Icon size={13} />
                            </span>
                            <span className="nb-drop-name">{s.label}</span>
                          </Link>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>

                {CENTER_LINKS.filter(l => !l.hasDrop).map(l => (
                  <Link key={l.id} to={l.href} className="nb-m-link"
                    onClick={() => setMobileOpen(false)}>
                    {l.label}
                  </Link>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
